import axios from 'axios';
import {
  clearAuthStorage,
  getRememberMe,
  getStoredToken,
  getStoredUser,
  setStoredToken,
} from '../utils/authStorage';

// Наш базовый URL API должен быть определен ДО настройки интерцепторов
// Приоритет: если мы внутри /p/:slug → всегда используем проектный API URL;
// иначе: переменная окружения → origin/api/v1
// Базовый адрес бэкенда: автоопределение через window.location.origin (без фоллбэков)
const FRONT_BASE = (() => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin.replace(/\/+$/, '');
  }
  throw new Error('FRONT_BASE: window.location is not available');
})();

// Нормализация URL: удаляет повторные слэши, не трогая протокол
// normalize double slashes except after protocol (http://)
const normalizeUrl = (u) => (u || '').replace(/([^:])\/\/+/g, '$1/');
const API_URL = (() => {
  try {
    const parts = typeof window !== 'undefined' ? window.location.pathname.split('/').filter(Boolean) : [];
    if (parts.length >= 2 && parts[0] === 'p') {
      const slug = parts[1];
      return `${FRONT_BASE}/api/p/${slug}`;
    }
  } catch (_e) { /* noop */ }
  return `${FRONT_BASE}/api`;
})();

const getCurrentProjectSlug = () => {
  try {
    const parts = typeof window !== 'undefined' ? window.location.pathname.split('/').filter(Boolean) : [];
    if (parts.length >= 2 && parts[0] === 'p') return parts[1];
  } catch (_e) { /* noop */ }
  return null;
};

// Глобальная защита от многократного параллельного refresh
let isRefreshing = false;
let refreshSubscribers = [];
let lastRefreshAt = 0; // для троттлинга повторных refresh подряд
const subscribeTokenRefresh = (cb) => refreshSubscribers.push(cb);
const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

// Во избежание двойной регистрации интерцепторов при HMR/повторном импорте
const installInterceptorsOnce = () => {
  
  if (typeof window !== 'undefined' && window.__AXIOS_INTERCEPTORS_CONFIGURED__) {
    return;
  }
  

  // Глобальный буфер дебага запросов
  if (typeof window !== 'undefined') {
    window.__API_DEBUG_LOGS__ = Array.isArray(window.__API_DEBUG_LOGS__) ? window.__API_DEBUG_LOGS__ : [];
  }

  const pushApiLog = (entry) => {
    try {
      if (typeof window === 'undefined') return;
      const logs = window.__API_DEBUG_LOGS__ || [];
      logs.push({ time: new Date().toISOString(), ...entry });
      while (logs.length > 100) logs.shift();
      window.__API_DEBUG_LOGS__ = logs;
    } catch (_) { /* noop */ }
  };

  axios.interceptors.request.use(
    (config) => {
      const token = getStoredToken();
      const url = config.baseURL ? `${config.baseURL}${config.url || ''}` : (config.url || '');
      const isOurApi = typeof url === 'string' && url.startsWith(FRONT_BASE);
      
      
      // Не добавляем Authorization на /auth/login и /auth/refresh
      const isAuthLoginReq = typeof url === 'string' && url.includes('/auth/login');
      const isAuthRefreshReq = typeof url === 'string' && url.includes('/auth/refresh');
      if (token && isOurApi && !isAuthLoginReq && !isAuthRefreshReq) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Логируем запрос
      pushApiLog({
        type: 'request',
        method: (config.method || 'GET').toUpperCase(),
        url,
        baseURL: config.baseURL || '',
        isOurApi,
        hasAuth: !!(config.headers && config.headers.Authorization),
        data: config.data || null,
        params: config.params || null,
      });
      return config;
    },
    (error) => Promise.reject(error)
  );

// Добавляем response interceptor для обработки 401 ошибок (c единственным refresh-потоком)
  axios.interceptors.response.use(
    (response) => {
      try {
        const url = response?.config?.baseURL ? `${response.config.baseURL}${response.config.url || ''}` : (response?.config?.url || '');
        pushApiLog({
          type: 'response',
          url,
          status: response?.status,
          data: response?.data,
        });
      } catch (_) { /* noop */ }
      return response;
    },
    async (error) => {
      const originalRequest = error.config || {};
      const url = originalRequest?.baseURL ? `${originalRequest.baseURL}${originalRequest.url || ''}` : (originalRequest?.url || '');
      const isOurApi = typeof url === 'string' && (url.startsWith(API_URL) || url.startsWith(FRONT_BASE));

      // Лог ошибок
      pushApiLog({
        type: 'error',
        url,
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      
      // Обрабатываем refresh ТОЛЬКО для нашего API, чтобы внешние 401 не вызывали лавину refresh
      if (isOurApi && error.response?.status === 401 && !originalRequest._retry) {
        // Не запускаем refresh-механику для запросов логина и запросов без токена
        const hasAuthHeader = !!(originalRequest.headers && originalRequest.headers.Authorization);
        const isAuthLogin = typeof url === 'string' && url.includes('/auth/login');
        if (!hasAuthHeader || isAuthLogin) {
          return Promise.reject(error);
        }
        // Помечаем один раз перед реальным refresh
        originalRequest._retry = true;
        
        // Не пытаемся обновить токен для самого запроса обновления токена
        if (url && url.includes('/auth/refresh')) {
          clearAuthStorage();
          
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
          return Promise.reject(error);
        }

        const now = Date.now();
        // Если мы уже делали refresh совсем недавно (< 3s), не запускаем новый цикл
        if (now - lastRefreshAt < 3000) {
          return new Promise((resolve) => {
            subscribeTokenRefresh((newToken) => {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(axios(originalRequest));
            });
          });
        }
        
        // Пытаемся обновить токен (единственным потоком), остальные запросы ждут
        const userData = getStoredUser() || {};
        const rememberMe = getRememberMe();
        
        
        if (!userData.id) {
          // Нет данных — выходим на логин
          clearAuthStorage();
          // Определяем куда редиректить при ошибке refresh token
          const currentPath = window.location.pathname;
          
          if (currentPath.startsWith('/p/')) {
            // Если мы в проекте - редиректим на проектный логин
            const pathMatch = currentPath.match(/\/p\/([^/]+)/);
            const projectSlug = pathMatch ? pathMatch[1] : 'demo';
            setTimeout(() => {
              window.location.href = `/p/${projectSlug}/login`;
            }, 5000);
          } else {
            // Если мы в глобальном контексте - редиректим на корень
            setTimeout(() => {
              window.location.href = '/';
            }, 5000);
          }
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // Ждём, пока первый поток обновит токен, затем повторяем запрос
          return new Promise((resolve) => {
            subscribeTokenRefresh((newToken) => {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(axios(originalRequest));
            });
          });
        }

        isRefreshing = true;
        try {
          const refreshAxios = axios.create();
          
          // Выбираем GLOBAL refresh для support/superadmin без projectSlug, иначе PROJECT refresh
          const currentToken = getStoredToken();
          const pathMatch = window.location.pathname.match(/\/p\/([^/]+)/);
          const urlProjectSlug = pathMatch ? pathMatch[1] : null;
          const role = userData.role;
          const isGlobalRole = role === 'support' || role === 'superadmin';
          const base = window.location.origin;
          const refreshUrl = isGlobalRole
            ? `${base}/api/auth/refresh`
            : `${base}/api/p/${(userData.projectSlug || urlProjectSlug || 'demo')}/auth/refresh`;
          
          
          const refreshResponse = await refreshAxios.post(refreshUrl, { rememberMe }, {
            headers: {
              Authorization: `Bearer ${currentToken}`
            }
          });
          
          const { access_token } = refreshResponse.data || {};
          if (!access_token) {
            console.error('❌ [REFRESH TOKEN] No access_token in refresh response');
            console.error('❌ [REFRESH TOKEN] Response data:', refreshResponse.data);
            throw new Error('No access_token in refresh response');
          }


          setStoredToken(access_token, rememberMe);

          lastRefreshAt = Date.now();
          onRefreshed(access_token);
          

          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return axios(originalRequest);
        } catch (refreshError) {
          console.error('❌ [REFRESH TOKEN] Failed to refresh token:', refreshError);
          console.error('❌ [REFRESH TOKEN] Refresh error details:', {
            message: refreshError.message,
            response: refreshError.response?.data,
            status: refreshError.response?.status,
            currentPath: window.location.pathname,
            userData: userData
          });
          console.error('❌ [REFRESH TOKEN] Full error object:', refreshError);
          clearAuthStorage();
          // Определяем куда редиректить при ошибке refresh token
          const currentPath = window.location.pathname;
          
          if (currentPath.startsWith('/p/')) {
            // Если мы в проекте - редиректим на проектный логин
            const pathMatch = currentPath.match(/\/p\/([^/]+)/);
            const projectSlug = pathMatch ? pathMatch[1] : 'demo';
            setTimeout(() => {
              window.location.href = `/p/${projectSlug}/login`;
            }, 5000);
          } else {
            // Если мы в глобальном контексте - редиректим на корень
            setTimeout(() => {
              window.location.href = '/';
            }, 5000);
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
        
        // Если обновление токена не удалось - очищаем хранилище и перенаправляем на логин
      }
      return Promise.reject(error);
    }
  );

  if (typeof window !== 'undefined') {
    window.__AXIOS_INTERCEPTORS_CONFIGURED__ = true;
  }
};

const getToken = () => getStoredToken();

// Периодический, редкий auto-refresh токена (по умолчанию каждые 10 минут)
const AUTO_REFRESH_MINUTES = parseInt(process.env.REACT_APP_TOKEN_REFRESH_MINUTES || '10', 10);
let tokenRefreshIntervalId = null;
const startAutoRefresh = () => {
  if (tokenRefreshIntervalId) return;
  tokenRefreshIntervalId = setInterval(async () => {
    try {
      const userData = getStoredUser() || {};
      const rememberMe = getRememberMe();
      if (!userData?.id || isRefreshing) return;

      // Мягкий авто-рефреш раз в N минут
      isRefreshing = true;
      const refreshAxios = axios.create();
      const urlSlug = getCurrentProjectSlug();
      const role = userData.role;
      const isGlobalRole = role === 'support' || role === 'superadmin';
      const base = window.location.origin;
      const currentToken = getStoredToken();
      const refreshUrl = isGlobalRole ? `${base}/api/auth/refresh` : `${base}/api/p/${(urlSlug || userData.projectSlug || 'demo')}/auth/refresh`;
      const refreshResponse = await refreshAxios.post(refreshUrl, { rememberMe }, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      const { access_token } = refreshResponse.data || {};
      if (access_token) {
        setStoredToken(access_token, rememberMe);
        onRefreshed(access_token);
        lastRefreshAt = Date.now();
      }
    } catch (e) {
      console.warn('Auto token refresh failed (will retry later):', e?.message || e);
    } finally {
      isRefreshing = false;
    }
  }, AUTO_REFRESH_MINUTES * 60 * 1000);
};

// Устанавливаем интерцепторы и авто-рефреш единоразово
// installInterceptorsOnce(); // Перенесено в конец файла
if (typeof window !== 'undefined') {
  if (!window.__TOKEN_AUTO_REFRESH_STARTED__) {
    startAutoRefresh();
    window.__TOKEN_AUTO_REFRESH_STARTED__ = true;
  }
}

// Глобальный API клиент для superadmin (catalog API)
const globalController = {
  // GET: catalog operations (renamed from 'get' to 'getGlobal' to avoid duplication)
  getGlobal: async (url) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const baseUrl = window.location.origin;
      const response = await axios.get(normalizeUrl(`${baseUrl}/api${url}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response;
    } catch (error) {
      console.error('globalController: GET error:', error.response?.data || error.message);
      throw error;
    }
  },
  // Alias for GET to keep API surface consistent
  get: async (url) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const baseUrl = window.location.origin;
      const response = await axios.get(normalizeUrl(`${baseUrl}/api${url}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response;
    } catch (error) {
      console.error('globalController: GET (alias) error:', error.response?.data || error.message);
      throw error;
    }
  },
  // POST: catalog operations
  post: async (url, data = {}) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const baseUrl = window.location.origin;
      const response = await axios.post(normalizeUrl(`${baseUrl}/api${url}`), data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response;
    } catch (error) {
      console.error('globalController: POST error:', error.response?.data || error.message);
      throw error;
    }
  },

  // Reload config
  reloadConfig: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const baseUrl = window.location.origin;
      const response = await axios.post(normalizeUrl(`${baseUrl}/api/reload-config`), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response;
    } catch (error) {
      console.error('globalController: Reload config error:', error.response?.data || error.message);
      throw error;
    }
  },

  // PUT: global operations
  put: async (url, data = {}) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const baseUrl = window.location.origin;
      const response = await axios.put(normalizeUrl(`${baseUrl}/api${url}`), data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response;
    } catch (error) {
      console.error('globalController: PUT error:', error.response?.data || error.message);
      throw error;
    }
  },

  // DELETE: global operations
  delete: async (url) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const baseUrl = window.location.origin;
      const response = await axios.delete(normalizeUrl(`${baseUrl}/api${url}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response;
    } catch (error) {
      console.error('globalController: DELETE error:', error.response?.data || error.message);
      throw error;
    }
  },
};

// Проектный API клиент (использует projectSlug)
const projectController = {
  // POST: логин без токена (специальный случай)
  postLogin: async (url, data, projectSlug = null) => {
    const slug = projectSlug || getCurrentProjectSlug();
    if (!slug) {
      throw new Error('Project context required - no project slug found in URL');
    }
    try {
      const baseUrl = window.location.origin;
      const response = await axios.post(normalizeUrl(`${baseUrl}/api/p/${slug}${url}`), data);
      return response;
    } catch (error) {
      console.error('projectController: POST login error:', error.response?.data || error.message);
      throw error;
    }
  },

  // GET: проектные данные (config.projectSlug — явный slug вне /p/:slug/…)
  get: async (url, config = {}) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    const { projectSlug: slugOverride, ...axiosConfig } = config;
    const slug = slugOverride || getCurrentProjectSlug();
    if (!slug) {
      throw new Error('Project context required - no project slug found in URL');
    }
    try {
      const baseUrl = window.location.origin;
      const response = await axios.get(normalizeUrl(`${baseUrl}/api/p/${slug}${url}`), {
        headers: { Authorization: `Bearer ${token}` },
        ...axiosConfig,
      });
      return response.data;
    } catch (error) {
      console.error('projectController: GET error:', error.response?.data || error.message);
      throw error;
    }
  },

  // POST: создание проектных данных (третий аргумент — явный projectSlug)
  post: async (url, data, projectSlugOverride = null) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    const slug = projectSlugOverride || getCurrentProjectSlug();
    if (!slug) {
      throw new Error('Project context required - no project slug found in URL');
    }
    try {
      const baseUrl = window.location.origin;
      const response = await axios.post(normalizeUrl(`${baseUrl}/api/p/${slug}${url}`), data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('projectController: POST error:', error.response?.data || error.message);
      throw error;
    }
  },

  // PUT: обновление проектных данных
  put: async (url, data) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    const slug = getCurrentProjectSlug();
    if (!slug) {
      throw new Error('Project context required - no project slug found in URL');
    }
    try {
      const baseUrl = window.location.origin;
      const response = await axios.put(normalizeUrl(`${baseUrl}/api/p/${slug}${url}`), data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('projectController: PUT error:', error.response?.data || error.message);
      throw error;
    }
  },

  // PATCH: частичное обновление проектных данных
  patch: async (url, data) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    const slug = getCurrentProjectSlug();
    if (!slug) {
      throw new Error('Project context required - no project slug found in URL');
    }
    try {
      const baseUrl = window.location.origin;
      const response = await axios.patch(normalizeUrl(`${baseUrl}/api/p/${slug}${url}`), data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('projectController: PATCH error:', error.response?.data || error.message);
      throw error;
    }
  },

  // DELETE: удаление проектных данных
  delete: async (url) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    const slug = getCurrentProjectSlug();
    if (!slug) {
      throw new Error('Project context required - no project slug found in URL');
    }
    try {
      const baseUrl = window.location.origin;
      const response = await axios.delete(normalizeUrl(`${baseUrl}/api/p/${slug}${url}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('projectController: DELETE error:', error.response?.data || error.message);
      throw error;
    }
  }
};

const controller = {
  checkUser: async (credentials) => {
    try {
      const base = window.location.origin;
      const response = await axios.post(normalizeUrl(`${base}/api/auth/login`), credentials);
      return response.data;
    } catch (error) {
      console.error('controller: checkUser error:', error.response?.data || error.message);
      throw error;
    }
  },
  registerSuperAdmin: async (payload) => {
    const base = window.location.origin;
    const response = await axios.post(
      normalizeUrl(`${base}/api/auth/register-superadmin`),
      payload,
    );
    return response.data;
  },
  getPublicAuthConfig: async () => {
    const base = window.location.origin;
    const response = await axios.get(
      normalizeUrl(`${base}/api/auth/public-config`),
    );
    return response.data;
  },
  resendVerificationEmail: async (payload) => {
    const base = window.location.origin;
    const response = await axios.post(
      normalizeUrl(`${base}/api/auth/resend-verification`),
      payload,
    );
    return response.data;
  },
  verifyEmailByToken: async (token) => {
    const base = window.location.origin;
    const response = await axios.get(
      normalizeUrl(
        `${base}/api/auth/verify-email?token=${encodeURIComponent(token)}`,
      ),
    );
    return response.data;
  },
  bindEmail: async (payload) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    const base = window.location.origin;
    const response = await axios.post(
      normalizeUrl(`${base}/api/auth/bind-email`),
      payload,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  },
  
  validateToken: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const userData = getStoredUser() || {};
      const role = userData?.role;
      const isGlobalRole = role === 'support' || role === 'superadmin';
      const base = window.location.origin;
      
      if (isGlobalRole) {
        const response = await axios.get(normalizeUrl(`${base}/api/auth/profile`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
      }

      // Определяем projectSlug из userData или из URL
      let projectSlug = null;
      if (userData.projectSlug) {
        projectSlug = userData.projectSlug;
      } else {
        const pathMatch = window.location.pathname.match(/\/p\/([^/]+)/);
        projectSlug = pathMatch ? pathMatch[1] : null;
      }
      
      if (!projectSlug) {
        throw new Error('Project context required for token validation');
      }
      
      
      // Проверяем токен через проектный endpoint
      const response = await axios.get(normalizeUrl(`${base}/api/p/${projectSlug}/auth/profile`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('controller: validateToken error:', error.response?.data || error.message);
      throw error;
    }
  },
  getData: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const slug = getCurrentProjectSlug();
      if (!slug) {
        throw new Error('Project context required - no project slug found in URL');
      }
      // Сначала пробуем «живой» снапшот /data, если недоступно — fallback на /stations
      let payload = [];
      try {
        const base = window.location.origin;
        const liveRes = await axios.get(normalizeUrl(`${base}/api/p/${slug}/data`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        payload = Array.isArray(liveRes.data) ? liveRes.data : [];
      } catch (_e) {
        console.warn('controller: /data not available, fallback to /stations');
        const cfgRes = await axios.get(`${API_URL}/stations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        payload = Array.isArray(cfgRes.data) ? cfgRes.data : [];
      }
      const nowMs = Date.now();
      
      // Конвертируем в legacy-формат для совместимости с существующими компонентами
      return payload.map((r) => {
        const updatedAtMs = r.updatedAt ? Number(r.updatedAt) : (r.updatedAtMs ? Number(r.updatedAtMs) : null);
        const updatedAtIso = updatedAtMs ? new Date(updatedAtMs).toISOString() : null;
        const hasConnection = typeof updatedAtMs === 'number';
        const timeSince = hasConnection ? Math.floor((nowMs - updatedAtMs) / 1000) : null;
        const alarmText = (r.alarmText && r.alarmText !== 'none') ? r.alarmText : '';
        return {
          Station_id: Number(r.Station_id ?? r.stationNum ?? r.id),
          Station_num: Number(r.Station_num ?? r.stationNum ?? r.num ?? r.id),
          Station_name: r.Station_name || r.name || `Станция ${r.stationNum ?? r.num ?? r.id}`,
          Station_description: r.Station_description || r.description || r.station_description || '',
          description: r.description || r.Station_description || r.station_description || '',
          stationId: r.stationId || r.Station_stationId || '',
          uuid: r.uuid || r.uid || r.uid_code || '',
          uid: r.uid || r.uid_code || r.uuid || '',
          uid_code: r.uid_code || r.uid || r.uuid || '',
          name: r.name || `Станция ${r.stationNum}`,
          Station_type: r.Station_type || r.type || 'type_1',
          Station_status: hasConnection ? (r.Station_status || r.status || '') : 'НЕТ СВЯЗИ',
          Station_alarm: alarmText,
          Station_connection: hasConnection ? 'СВЯЗЬ' : 'НЕТ СВЯЗИ',
          Station_SV: Number(r.Station_SV ?? r.sv) || 0,
          Station_PV: Number(r.Station_PV ?? r.pv) || 0,
          Station_PWM: Number(r.Station_PWM ?? r.pwm) || 0,
          Station_Amper: Number(r.Station_Amper ?? r.amper) || 0,
          Station_PV2: Number(r.Station_PV2 ?? r.pv2) || 0,
          Station_lastDataTime: updatedAtIso,
          Station_timeSinceLastData: timeSince,
          connectionLostSinceMs: hasConnection ? null : (updatedAtMs ?? null),
          activeAlarms: Array.isArray(r.activeAlarms) ? r.activeAlarms : (alarmText ? alarmText.split(',').map(s => s.trim()).filter(Boolean).map(msg => ({ message: msg, severity: /затопл|warning|сенсор/i.test(msg) ? 'WARNING' : 'ERROR', isActive: true })) : []),
        };
      });
    } catch (error) {
      console.error('controller: getData error:', error.response?.data || error.message);
      throw error;
    }
  },
  getSets: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.get('/station-sets');
      
      // Обработка нового формата stations: [{"uid": "uuid", "num": 1}]
      // Поддержка старого формата: [1, 2, 3] для обратной совместимости
      if (Array.isArray(response)) {
        return response.map((set) => {
          // Если stations уже в новом формате, возвращаем как есть
          if (set.stations && Array.isArray(set.stations)) {
            let stationsField = set.stations;
            
            // Парсим строку, если это JSON
            if (typeof stationsField === 'string') {
              try {
                stationsField = JSON.parse(stationsField);
              } catch (e) {
                console.warn('controller: Failed to parse stations JSON:', e);
                stationsField = [];
              }
            }
            
            // Проверяем формат: если первый элемент - объект с uid, то новый формат
            // Если первый элемент - число, то старый формат (оставляем как есть для обратной совместимости)
            if (stationsField.length > 0) {
              const firstItem = stationsField[0];
              if (typeof firstItem === 'object' && firstItem.uid && firstItem.num !== undefined) {
                // Новый формат - возвращаем как есть
                return { ...set, stations: stationsField };
              }
            }
            
            // Старый формат - возвращаем как есть (компоненты обработают конвертацию)
            return set;
          }
          
          return set;
        });
      }
      
      return response;
    } catch (error) {
      console.error('controller: getSets error:', error.response?.data || error.message);
      throw error;
    }
  },
  createSet: async (setData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.post('/station-sets', setData);
      return response;
    } catch (error) {
      console.error('controller: createSet error:', error.response?.data || error.message);
      throw error;
    }
  },
  updateSet: async (id, data) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.put(`/station-sets/${id}`, data);
      return response;
    } catch (error) {
      console.error('controller: updateSet error:', error.response?.data || error.message);
      throw error;
    }
  },
  deleteSet: async (setId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.delete(`/station-sets/${setId}`);
      return response;
    } catch (error) {
      console.error('controller: deleteSet error:', error.response?.data || error.message);
      throw error;
    }
  },
  getUsers: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.get('/users');
      return response;
    } catch (error) {
      console.error('controller: getUsers error:', error);
      console.error('controller: getUsers error response:', error.response);
      throw error;
    }
  },
  createUser: async (userData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.post('/users', userData);
      return response;
    } catch (error) {
      console.error('controller: createUser error:', error.response?.data || error.message);
      throw error;
    }
  },
  updateUser: async (userId, userData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.patch(`/users/${userId}`, userData);
      return response;
    } catch (error) {
      console.error('controller: updateUser error:', error.response?.data || error.message);
      throw error;
    }
  },
  deleteUser: async (userId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.delete(`/users/${userId}`);
      return response;
    } catch (error) {
      console.error('controller: deleteUser error:', error.response?.data || error.message);
      throw error;
    }
  },
  updateUserDefaultGroup: async (userId, defaultGroupId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.put(`/users/${userId}/default-group`, { defaultGroupId });
      return response;
    } catch (error) {
      console.error('controller: updateUserDefaultGroup error:', error.response?.data || error.message);
      throw error;
    }
  },
  getSetGroups: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.get('/set-groups');
      return response;
    } catch (error) {
      console.error('controller: getSetGroups error:', error.response?.data || error.message);
      throw error;
    }
  },
  getSetGroupById: async (id) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.get(`/set-groups/${id}`);
      return response;
    } catch (error) {
      console.error('controller: getSetGroupById error:', error.response?.data || error.message);
      throw error;
    }
  },
  createSetGroup: async (groupData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.post('/set-groups', groupData);
      return response;
    } catch (error) {
      console.error('controller: createSetGroup error:', error.response?.data || error.message);
      throw error;
    }
  },
  updateSetGroup: async (id, groupData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.put(`/set-groups/${id}`, groupData);
      return response;
    } catch (error) {
      console.error('controller: updateSetGroup error:', error.response?.data || error.message);
      throw error;
    }
  },
  deleteSetGroup: async (id) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.delete(`/set-groups/${id}`);
      return response;
    } catch (error) {
      console.error('controller: deleteSetGroup error:', error.response?.data || error.message);
      throw error;
    }
  },
  getSetById: async (setId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.get(`/station-sets/${setId}`);
      return response;
    } catch (error) {
      console.error('controller: getSetById error:', error.response?.data || error.message);
      throw error;
    }
  },
  getMainConfig: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.get('/main-config');
      return response;
    } catch (error) {
      console.error('controller: getMainConfig error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Получает список станций проекта из каталога
   * SPRINT_02: Использует uid_station из каталога
   * @returns {Promise} Массив станций
   */
  getStations: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const slug = getCurrentProjectSlug();
      if (!slug) {
        throw new Error('Project context required - no project slug found in URL');
      }
      
      // SPRINT_02: Получаем станции из каталога через проектный API
      const response = await projectController.get('/stations');
      
      // SPRINT_02: Ответ теперь массив станций из каталога
      const stationsArray = Array.isArray(response) ? response : [];
      
      // Нормализуем в legacy-формат для совместимости, но добавляем новые поля
      const normalized = stationsArray.map((r) => {
        const num = Number(r.num ?? r.stationNum ?? r.id);
        const pk = Number(r.id ?? num);
        return {
          id: num,               // Техномер как ключ
          name: r.name,
          Station_name: r.name,
          Station_id: pk,        // Реальный PK если присутствует
          num,
          type: Number(r.type ?? 0),
          sort: Number(r.sort ?? 0),
          // Новые поля для мульти-UID
          uid: r.uid_code || null,
          uid_code: r.uid_code || null,
          stationId: r.uid_code ? `${r.uid_code}:${num}` : null,
          map_lat:
            r.map_lat !== undefined && r.map_lat !== null
              ? Number(r.map_lat)
              : null,
          map_lng:
            r.map_lng !== undefined && r.map_lng !== null
              ? Number(r.map_lng)
              : null,
        };
      });
      
      return normalized;
    } catch (error) {
      console.error('controller: getStations error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Получает список станций для UID из каталога
   * SPRINT_02: Новый метод для работы с каталогом
   * @param {number} uidId - ID UID профиля
   * @returns {Promise} Массив станций
   */
  getStationsByUid: async (uidId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.getGlobal(`/catalog/uids/${uidId}/stations`);
      return response.items || [];
    } catch (error) {
      console.error('controller: getStationsByUid error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Получает список станций суперадмина из каталога
   * SPRINT_02: Новый метод для работы с каталогом
   * @param {number} superAdminId - ID суперадмина
   * @returns {Promise} Массив станций
   */
  getStationsBySuperAdmin: async (superAdminId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.getGlobal(`/catalog/superadmins/${superAdminId}/stations`);
      return response.items || [];
    } catch (error) {
      console.error('controller: getStationsBySuperAdmin error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Получает список UID для проекта из каталога
   * SPRINT_02: Новый метод для получения UID проекта
   * @param {string} projectSlug - slug проекта (не используется, берется из URL)
   * @returns {Promise} Массив UID с id, code, name
   */
  getUidsForProject: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.get(`/uids`);
      // Ответ - массив UID из каталога
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('controller: getUidsForProject error:', error.response?.data || error.message);
      throw error;
    }
  },
  getStationById: async (id) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.get(`/stations/${id}`);
      return response;
    } catch (error) {
      console.error('controller: getStationById error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Создает станцию в каталоге
   * SPRINT_02: Создание через проектное API с проверкой UID
   * @param {Object} stationData - данные станции {uidId, num, name, type, sort}
   * @returns {Promise} ID созданной станции
   */
  createStation: async (stationData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.post('/stations', stationData);
      return response;
    } catch (error) {
      console.error('controller: createStation error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Обновляет станцию в каталоге
   * SPRINT_02: Обновление через проектное API
   * @param {number} id - ID станции
   * @param {Object} stationData - данные для обновления
   * @returns {Promise}
   */
  updateStation: async (id, stationData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.patch(`/stations/${id}`, stationData);
      return response;
    } catch (error) {
      console.error('controller: updateStation error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Удаляет станцию из каталога
   * SPRINT_02: Удаление через проектное API
   * @param {number} id - ID станции
   * @returns {Promise}
   */
  deleteStation: async (id) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.delete(`/stations/${id}`);
      return response;
    } catch (error) {
      console.error('controller: deleteStation error:', error.response?.data || error.message);
      throw error;
    }
  },
  getStationsStats: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.get('/stations/stats');
      return response;
    } catch (error) {
      console.error('controller: getStationsStats error:', error.response?.data || error.message);
      throw error;
    }
  },
  // ==================== SPRINT_03: Глобальные теги (uid_tag_definition) ====================
  /**
   * Получает список тегов для UID из каталога
   * SPRINT_03: Новый метод для работы с каталогом
   * @param {number} uidId - ID UID профиля
   * @returns {Promise} Массив тегов
   */
  getTagsConfig: async (uidId, opts = {}) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      if (opts?.role === 'admin') {
        const data = await projectController.get(`/uids/${uidId}/tags`);
        return Array.isArray(data) ? data : (data?.items || []);
      }
      const response = await globalController.getGlobal(`/catalog/uids/${uidId}/tags`);
      return response.data?.items || [];
    } catch (error) {
      console.error('controller: getTagsConfig error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Получает список тегов для проекта
   * SPRINT_03: Новый метод для получения тегов проекта
   * @param {string} projectSlug - slug проекта (не используется, берется из URL)
   * @returns {Promise} Массив тегов
   */
  getTagsForProject: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const slug = getCurrentProjectSlug();
      if (!slug) {
        throw new Error('Project context required - no project slug found in URL');
      }
      const response = await projectController.get('/tags');
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('controller: getTagsForProject error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Создает тег в каталоге
   * SPRINT_03: Создание через проектное API с проверкой UID
   * @param {Object} tagData - данные тега {uidId, var, descr, packet_offset, ...}
   * @returns {Promise} ID созданного тега
   */
  createTag: async (tagData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.post('/tags', tagData);
      return response;
    } catch (error) {
      console.error('controller: createTag error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Обновляет тег в каталоге
   * SPRINT_03: Обновление через проектное API
   * @param {number} tagId - ID тега
   * @param {Object} tagData - данные для обновления
   * @returns {Promise}
   */
  updateTag: async (tagId, tagData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.put(`/tags/${tagId}`, tagData);
      return response;
    } catch (error) {
      console.error('controller: updateTag error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Удаляет тег из каталога
   * SPRINT_03: Удаление через проектное API
   * @param {number} tagId - ID тега
   * @returns {Promise}
   */
  deleteTag: async (tagId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.delete(`/tags/${tagId}`);
      return response;
    } catch (error) {
      console.error('controller: deleteTag error:', error.response?.data || error.message);
      throw error;
    }
  },
  // ==================== SPRINT_04: Глобальные карточки (uid_component_template) ====================
  /**
   * Получает список карточек для UID из каталога
   * SPRINT_04: Новый метод для работы с каталогом
   * @param {number} uidId - ID UID профиля
   * @returns {Promise} Массив карточек
   */
  getComponentsByUid: async (uidId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.getGlobal(`/catalog/uids/${uidId}/components`);
      return response.data?.items || [];
    } catch (error) {
      console.error('controller: getComponentsByUid error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Получает список карточек для проекта
   * SPRINT_04: Новый метод для получения карточек проекта
   * @param {string} projectSlug - slug проекта (не используется, берется из URL)
   * @returns {Promise} Массив карточек
   */
  getComponentsForProject: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const slug = getCurrentProjectSlug();
      if (!slug) {
        throw new Error('Project context required - no project slug found in URL');
      }
      const response = await projectController.get('/components');
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('controller: getComponentsForProject error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Создает карточку в каталоге
   * SPRINT_04: Создание через каталог API (globalController)
   * @param {number} uidId - ID UID профиля
   * @param {Object} componentData - данные карточки {name, component_type, layout, pseudocode, field_settings, table_settings, list_settings}
   * @returns {Promise} ID созданной карточки
   */
  createComponent: async (uidId, componentData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.post(`/catalog/uids/${uidId}/components`, componentData);
      return response.data || response;
    } catch (error) {
      console.error('controller: createComponent error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Обновляет карточку в каталоге
   * SPRINT_04: Обновление через каталог API (globalController)
   * @param {number} componentId - ID карточки
   * @param {Object} componentData - данные для обновления
   * @returns {Promise}
   */
  updateComponent: async (componentId, componentData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.put(`/catalog/components/${componentId}`, componentData);
      return response.data || response;
    } catch (error) {
      console.error('controller: updateComponent error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Удаляет карточку из каталога
   * SPRINT_04: Удаление через каталог API (globalController)
   * @param {number} componentId - ID карточки
   * @returns {Promise}
   */
  deleteComponent: async (componentId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.delete(`/catalog/components/${componentId}`);
      return response.data || response;
    } catch (error) {
      console.error('controller: deleteComponent error:', error.response?.data || error.message);
      throw error;
    }
  },
  // ==================== SPRINT_05: Глобальные правила аварий (uid_alarm_rule) ====================
  /**
   * Получает список правил аварий для UID из каталога
   * SPRINT_05: Новый метод для работы с каталогом
   * @param {number} uidId - ID UID профиля
   * @returns {Promise} Массив правил
   */
  getAlarmRules: async (uidId, opts = {}) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      if (opts?.role === 'admin') {
        const data = await projectController.get(`/uids/${uidId}/alarm-rules`);
        return Array.isArray(data) ? data : (data?.items || []);
      }
      const response = await globalController.getGlobal(`/catalog/uids/${uidId}/alarm-rules`);
      return response.data.items || [];
    } catch (error) {
      console.error('controller: getAlarmRules error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Получает список правил аварий для проекта
   * SPRINT_05: Новый метод для получения правил проекта
   * @param {string} projectSlug - slug проекта
   * @returns {Promise} Массив правил
   */
  getAlarmRulesForProject: async (projectSlug) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.getGlobal(`/catalog/projects/${projectSlug}/alarm-rules`);
      return response.data.items || [];
    } catch (error) {
      console.error('controller: getAlarmRulesForProject error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Создает правило аварии в каталоге
   * SPRINT_05: Создание через каталог API
   * @param {number} uidId - ID UID профиля
   * @param {Object} ruleData - данные правила {tag_definition_id, name, severity, rule_type, ...}
   * @returns {Promise} ID созданного правила
   */
  createAlarmRule: async (uidId, ruleData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.post(`/catalog/uids/${uidId}/alarm-rules`, ruleData);
      return response.data;
    } catch (error) {
      console.error('controller: createAlarmRule error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Импортирует правила аварий из CSV
   * @param {number} uidId - ID UID профиля
   * @param {Object} payload - { csv, skip_existing, update_existing }
   * @returns {Promise} Результат импорта
   */
  importAlarmRulesCsv: async (uidId, payload) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.post(`/catalog/uids/${uidId}/alarm-rules/import`, payload);
      return response.data || response;
    } catch (error) {
      console.error('controller: importAlarmRulesCsv error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Обновляет правило аварии в каталоге
   * SPRINT_05: Обновление через каталог API
   * @param {number} ruleId - ID правила
   * @param {Object} ruleData - данные для обновления
   * @returns {Promise}
   */
  updateAlarmRule: async (ruleId, ruleData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.put(`/catalog/alarm-rules/${ruleId}`, ruleData);
      return response.data;
    } catch (error) {
      console.error('controller: updateAlarmRule error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Удаляет правило аварии из каталога
   * SPRINT_05: Удаление через каталог API
   * @param {number} ruleId - ID правила
   * @returns {Promise}
   */
  deleteAlarmRule: async (ruleId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.delete(`/catalog/alarm-rules/${ruleId}`);
      return response.data;
    } catch (error) {
      console.error('controller: deleteAlarmRule error:', error.response?.data || error.message);
      throw error;
    }
  },

  // ==================== TREND CONFIGS (SPRINT_06) ====================

  /**
   * SPRINT_06: Получает список настроек трендов для UID из каталога
   * @param {number} uidId - ID UID профиля
   * @returns {Promise} Массив настроек
   */
  getTrendConfigs: async (uidId, opts = {}) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      if (opts?.role === 'admin') {
        const data = await projectController.get(`/uids/${uidId}/trend-configs`);
        return Array.isArray(data) ? data : (data?.items || []);
      }
      const response = await globalController.get(`/catalog/uids/${uidId}/trend-configs`);
      return response.data.items || [];
    } catch (error) {
      console.error('controller: getTrendConfigs error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * SPRINT_06: Получает список настроек трендов для проекта
   * @param {string} projectSlug - slug проекта
   * @returns {Promise} Массив настроек
   */
  getTrendConfigsForProject: async (projectSlug) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.get(`/catalog/projects/${projectSlug}/trend-configs`);
      return response.data.items || [];
    } catch (error) {
      console.error('controller: getTrendConfigsForProject error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * SPRINT_06: Создает настройку тренда в каталоге
   * @param {number} uidId - ID UID профиля
   * @param {Object} configData - данные настройки {tag_definition_id, is_trended, min_period_seconds, deadband}
   * @returns {Promise} ID созданной настройки
   */
  createTrendConfig: async (uidId, configData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.post(`/catalog/uids/${uidId}/trend-configs`, configData);
      return response.data;
    } catch (error) {
      console.error('controller: createTrendConfig error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * SPRINT_06: Обновляет настройку тренда в каталоге
   * @param {number} configId - ID настройки
   * @param {Object} configData - данные для обновления
   * @returns {Promise}
   */
  updateTrendConfig: async (configId, configData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.put(`/catalog/trend-configs/${configId}`, configData);
      return response.data;
    } catch (error) {
      console.error('controller: updateTrendConfig error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * SPRINT_06: Удаляет настройку тренда из каталога
   * @param {number} configId - ID настройки
   * @returns {Promise}
   */
  deleteTrendConfig: async (configId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.delete(`/catalog/trend-configs/${configId}`);
      return response.data;
    } catch (error) {
      console.error('controller: deleteTrendConfig error:', error.response?.data || error.message);
      throw error;
    }
  },
  // ==================== SPRINT_07: Глобальные группы кодов (uid_code_group) ====================
  /**
   * Получает список групп кодов для UID из каталога
   * @param {number} uidId - ID UID профиля
   * @returns {Promise} Массив групп
   */
  getCodeGroups: async (uidId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.getGlobal(`/catalog/uids/${uidId}/code-groups`);
      return response.data?.items || [];
    } catch (error) {
      console.error('controller: getCodeGroups error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Получает группу кодов по ID с элементами
   * @param {number} groupId - ID группы
   * @returns {Promise} Группа с элементами
   */
  getCodeGroupById: async (groupId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.getGlobal(`/catalog/code-groups/${groupId}`);
      return response.data;
    } catch (error) {
      console.error('controller: getCodeGroupById error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Создает группу кодов в каталоге
   * @param {number} uidId - ID UID профиля
   * @param {Object} groupData - данные группы {name, type}
   * @returns {Promise} ID созданной группы
   */
  createCodeGroup: async (uidId, groupData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.post(`/catalog/uids/${uidId}/code-groups`, groupData);
      return response.data;
    } catch (error) {
      console.error('controller: createCodeGroup error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Обновляет группу кодов в каталоге
   * @param {number} groupId - ID группы
   * @param {Object} groupData - данные для обновления
   * @returns {Promise}
   */
  updateCodeGroup: async (groupId, groupData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.put(`/catalog/code-groups/${groupId}`, groupData);
      return response.data;
    } catch (error) {
      console.error('controller: updateCodeGroup error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Удаляет группу кодов из каталога
   * @param {number} groupId - ID группы
   * @returns {Promise}
   */
  deleteCodeGroup: async (groupId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.delete(`/catalog/code-groups/${groupId}`);
      return response.data;
    } catch (error) {
      console.error('controller: deleteCodeGroup error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Получает список элементов группы кодов
   * @param {number} groupId - ID группы
   * @returns {Promise} Массив элементов
   */
  getCodeItems: async (groupId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.getGlobal(`/catalog/code-groups/${groupId}/items`);
      return response.data?.items || [];
    } catch (error) {
      console.error('controller: getCodeItems error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Создает элемент группы кодов
   * @param {number} groupId - ID группы
   * @param {Object} itemData - данные элемента
   * @returns {Promise} ID созданного элемента
   */
  createCodeItem: async (groupId, itemData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.post(`/catalog/code-groups/${groupId}/items`, itemData);
      return response.data;
    } catch (error) {
      console.error('controller: createCodeItem error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Обновляет элемент группы кодов
   * @param {number} itemId - ID элемента
   * @param {Object} itemData - данные для обновления
   * @returns {Promise}
   */
  updateCodeItem: async (itemId, itemData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.put(`/catalog/code-items/${itemId}`, itemData);
      return response.data;
    } catch (error) {
      console.error('controller: updateCodeItem error:', error.response?.data || error.message);
      throw error;
    }
  },
  /**
   * Удаляет элемент группы кодов
   * @param {number} itemId - ID элемента
   * @returns {Promise}
   */
  deleteCodeItem: async (itemId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.delete(`/catalog/code-items/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('controller: deleteCodeItem error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Получает список шаблонов команд для UID из каталога
   * @param {number} uidId - ID UID профиля
   * @returns {Promise} Массив шаблонов
   */
  getCommandTemplates: async (uidId, opts = {}) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      if (opts?.role === 'admin') {
        const data = await projectController.get(`/uids/${uidId}/command-templates`);
        return Array.isArray(data) ? data : (data?.items || []);
      }
      const response = await globalController.getGlobal(`/catalog/uids/${uidId}/command-templates`);
      return response.data?.items || [];
    } catch (error) {
      console.error('controller: getCommandTemplates error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Получает список шаблонов команд для проекта
   * @param {string} projectSlug - slug проекта
   * @returns {Promise} Массив шаблонов
   */
  getCommandTemplatesForProject: async (projectSlug) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.getGlobal(`/catalog/projects/${projectSlug}/command-templates`);
      return response.data?.items || [];
    } catch (error) {
      console.error('controller: getCommandTemplatesForProject error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Создает шаблон команды в каталоге
   * @param {number} uidId - ID UID профиля
   * @param {Object} templateData - данные шаблона {name, body_hex, description, is_active}
   * @returns {Promise} ID созданного шаблона
   */
  createCommandTemplate: async (uidId, templateData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.post(`/catalog/uids/${uidId}/command-templates`, templateData);
      return response.data;
    } catch (error) {
      console.error('controller: createCommandTemplate error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Обновляет шаблон команды в каталоге
   * @param {number} templateId - ID шаблона
   * @param {Object} templateData - данные для обновления
   * @returns {Promise}
   */
  updateCommandTemplate: async (templateId, templateData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.put(`/catalog/command-templates/${templateId}`, templateData);
      return response.data;
    } catch (error) {
      console.error('controller: updateCommandTemplate error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Удаляет шаблон команды из каталога
   * @param {number} templateId - ID шаблона
   * @returns {Promise}
   */
  deleteCommandTemplate: async (templateId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.delete(`/catalog/command-templates/${templateId}`);
      return response.data;
    } catch (error) {
      console.error('controller: deleteCommandTemplate error:', error.response?.data || error.message);
      throw error;
    }
  },

  // ==================== SVG CONFIGURATIONS ====================

  /**
   * Получает список SVG конфигураций для UID
   * @param {number} uidId - ID UID
   * @returns {Promise} Массив конфигураций
   */
  getSvgConfigs: async (uidId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.getGlobal(`/catalog/uids/${uidId}/svg-configs`);
      return response.data?.items || [];
    } catch (error) {
      console.error('controller: getSvgConfigs error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Получает конкретную SVG конфигурацию
   * @param {number} uidId - ID UID
   * @param {number} configId - ID конфигурации
   * @returns {Promise} Конфигурация
   */
  getSvgConfig: async (uidId, configId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.getGlobal(`/catalog/uids/${uidId}/svg-configs/${configId}`);
      return response.data;
    } catch (error) {
      console.error('controller: getSvgConfig error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Создает SVG конфигурацию
   * @param {number} uidId - ID UID
   * @param {Object} configData - данные конфигурации
   * @returns {Promise} ID созданной конфигурации
   */
  createSvgConfig: async (uidId, configData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.post(`/catalog/uids/${uidId}/svg-configs`, configData);
      return response.data;
    } catch (error) {
      console.error('controller: createSvgConfig error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Обновляет SVG конфигурацию
   * @param {number} uidId - ID UID
   * @param {number} configId - ID конфигурации
   * @param {Object} configData - данные для обновления
   * @returns {Promise}
   */
  updateSvgConfig: async (uidId, configId, configData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.put(`/catalog/uids/${uidId}/svg-configs/${configId}`, configData);
      return response.data;
    } catch (error) {
      console.error('controller: updateSvgConfig error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Удаляет SVG конфигурацию
   * @param {number} uidId - ID UID
   * @param {number} configId - ID конфигурации
   * @returns {Promise}
   */
  deleteSvgConfig: async (uidId, configId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.delete(`/catalog/uids/${uidId}/svg-configs/${configId}`);
      return response.data;
    } catch (error) {
      console.error('controller: deleteSvgConfig error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Парсит теги из SVG контента
   * @param {number} uidId - ID UID
   * @param {string} svgContent - содержимое SVG
   * @returns {Promise} Результат парсинга
   */
  parseSvgTags: async (uidId, svgContent) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await globalController.post(`/catalog/uids/${uidId}/svg-configs/parse-tags`, {
        svg_content: svgContent,
      });
      return response.data;
    } catch (error) {
      console.error('controller: parseSvgTags error:', error.response?.data || error.message);
      throw error;
    }
  },

  // ==================== SVG CONFIGURATIONS FOR USERS ====================

  /**
   * Получает список SVG конфигураций для UID в проекте
   * @param {string} projectSlug - slug проекта
   * @param {string} uidCode - код UID
   * @returns {Promise} Массив конфигураций
   */
  getSvgConfigsForProject: async (projectSlug, uidCode) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      // projectController.get уже возвращает response.data, а не response
      const data = await projectController.get(`/svg-configs/${uidCode}`);
      const items = data?.items || [];
      return items;
    } catch (error) {
      console.error('controller: getSvgConfigsForProject error:', error.response?.data || error.message);
      throw error;
    }
  },

  getAllSvgConfigsForProject: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const data = await projectController.get('/svg-configs');
      return data?.items || [];
    } catch (error) {
      console.error('controller: getAllSvgConfigsForProject error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Получает SVG конфигурацию для отображения
   * @param {string} projectSlug - slug проекта
   * @param {string} uidCode - код UID
   * @param {number} configId - ID конфигурации
   * @returns {Promise} Конфигурация SVG
   */
  getSvgConfigForDisplay: async (projectSlug, uidCode, configId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      // projectController.get уже возвращает response.data, а не response
      const data = await projectController.get(`/svg-configs/${uidCode}/${configId}`);
      return data;
    } catch (error) {
      console.error('controller: getSvgConfigForDisplay error:', error.response?.data || error.message);
      throw error;
    }
  },

  getSvgConfigForDisplayById: async (projectSlug, configId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const data = await projectController.get(`/svg-configs/config/${configId}`);
      return data;
    } catch (error) {
      console.error('controller: getSvgConfigForDisplayById error:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Получает текущие значения тегов для SVG
   * @param {string} projectSlug - slug проекта
   * @param {string} uidCode - код UID
   * @param {number} configId - ID конфигурации
   * @param {string[]} tags - список тегов (опционально)
   * @returns {Promise} Значения тегов и алармы
   */
  getSvgTagValues: async (projectSlug, uidCode, configId, tags) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const params = tags && tags.length > 0 ? { tags: tags.join(',') } : {};
      // projectController.get уже возвращает response.data, а не response
      const data = await projectController.get(`/svg-configs/${uidCode}/${configId}/tag-values`, { params });
      return data;
    } catch (error) {
      console.error('controller: getSvgTagValues error:', error.response?.data || error.message);
      throw error;
    }
  },

  getSvgTagValuesDirect: async (projectSlug, uidCode, tags) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const data = await projectController.post(
        `/svg-tag-values/${uidCode}`,
        { tags: Array.isArray(tags) ? tags : [] },
      );
      return data;
    } catch (error) {
      console.error('controller: getSvgTagValuesDirect error:', error.response?.data || error.message);
      throw error;
    }
  },

  getSvgTagValuesProjectWide: async (projectSlug, tags, uidCode = '') => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const data = await projectController.post(
        '/svg-tag-values',
        {
          tags: Array.isArray(tags) ? tags : [],
          uidCode: String(uidCode || ''),
        },
      );
      return data;
    } catch (error) {
      console.error('controller: getSvgTagValuesProjectWide error:', error.response?.data || error.message);
      throw error;
    }
  },

  /** CRUD SVG в БД проекта (явный projectSlug — для SuperAdmin и т.п.). */
  createProjectSvgConfig: async (projectSlug, uidCode, configData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    const url = normalizeUrl(
      `${FRONT_BASE}/api/p/${encodeURIComponent(projectSlug)}/svg-configs/${encodeURIComponent(uidCode)}`,
    );
    const response = await axios.post(url, configData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  updateProjectSvgConfig: async (projectSlug, uidCode, configId, configData) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    const url = normalizeUrl(
      `${FRONT_BASE}/api/p/${encodeURIComponent(projectSlug)}/svg-configs/${encodeURIComponent(uidCode)}/${configId}`,
    );
    const response = await axios.put(url, configData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  deleteProjectSvgConfig: async (projectSlug, uidCode, configId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    const url = normalizeUrl(
      `${FRONT_BASE}/api/p/${encodeURIComponent(projectSlug)}/svg-configs/${encodeURIComponent(uidCode)}/${configId}`,
    );
    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  parseSvgTagsForProject: async (projectSlug, uidCode, svgContent) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    const url = normalizeUrl(
      `${FRONT_BASE}/api/p/${encodeURIComponent(projectSlug)}/svg-configs/${encodeURIComponent(uidCode)}/parse-tags`,
    );
    const response = await axios.post(
      url,
      { svg_content: svgContent },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  importProjectSvgConfigsFromCatalog: async (projectSlug, uidCode) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    const url = normalizeUrl(
      `${FRONT_BASE}/api/p/${encodeURIComponent(projectSlug)}/svg-configs/${encodeURIComponent(uidCode)}/import-from-catalog`,
    );
    const response = await axios.post(url, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /**
   * Отправляет команду через TCP сервер используя шаблон из каталога
   * @param {string} projectSlug - slug проекта
   * @param {number} stationNum - номер станции
   * @param {number} commandTemplateId - ID шаблона команды
   * @returns {Promise} Результат отправки команды
   */
  sendCommand: async (projectSlug, stationNum, commandTemplateId) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.post('/command', {
        stationNum: stationNum,
        commandTemplateId: commandTemplateId,
      });
      return response.data;
    } catch (error) {
      console.error('controller: sendCommand error:', error.response?.data || error.message);
      throw error;
    }
  },

  // Debug endpoints
  getDebugLastPackets: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.get('/debug/packets/last');
      return response;
    } catch (error) {
      console.error('controller: getDebugLastPackets error:', error.response?.data || error.message);
      throw error;
    }
  },
  getDebugUnrecognizedPackets: async () => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.get('/debug/packets/unrecognized');
      return response;
    } catch (error) {
      console.error('controller: getDebugUnrecognizedPackets error:', error.response?.data || error.message);
      throw error;
    }
  },
  refreshToken: async (userId, rememberMe = false) => {
    try {
      const base = window.location.origin;
      const response = await axios.post(normalizeUrl(`${base}/api/auth/refresh`), { userId, rememberMe });
      return response.data;
    } catch (error) {
      console.error('controller: refreshToken error:', error.response?.data || error.message);
      throw error;
    }
  },
  // PUT: global operations
  put: async (url, data = {}) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const baseUrl = window.location.origin;
      const response = await axios.put(normalizeUrl(`${baseUrl}/api${url}`), data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response;
    } catch (error) {
      console.error('globalController: PUT error:', error.response?.data || error.message);
      throw error;
    }
  },
  // DELETE: global operations
  delete: async (url) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const baseUrl = window.location.origin;
      const response = await axios.delete(normalizeUrl(`${baseUrl}/api${url}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response;
    } catch (error) {
      console.error('globalController: DELETE error:', error.response?.data || error.message);
      throw error;
    }
  },
};

const controllerPut = {
  put: async (payload) => {
    const token = getToken();
    if (!token) throw new Error('No token found');
    try {
      const response = await projectController.put('/station-sets/command', payload);
      return response;
    } catch (error) {
      console.error('controllerPut: put error:', error.response?.data || error.message);
      throw error;
    }
  },
};

// Добавляем метод GET в основной контроллер
controller.get = async (url) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  try {
    // Для глобальных эндпоинтов используем базовый API_URL
    const baseUrl = window.location.origin;
      const response = await axios.get(normalizeUrl(`${baseUrl}/api${url}`), {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    console.error('controller: GET error:', error.response?.data || error.message);
    throw error;
  }
};

// Добавляем метод POST в основной контроллер
controller.post = async (url, data = {}) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  try {
    // Для глобальных эндпоинтов используем базовый API_URL
    const baseUrl = window.location.origin;
      const response = await axios.post(normalizeUrl(`${baseUrl}/api${url}`), data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    console.error('controller: POST error:', error.response?.data || error.message);
    throw error;
  }
};

// Добавляем метод DELETE (c телом) в основной контроллер для глобальных эндпоинтов
controller.delete = async (url, data = {}) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  try {
    const baseUrl = window.location.origin;
    const response = await axios.delete(normalizeUrl(`${baseUrl}/api${url}`), {
      headers: { Authorization: `Bearer ${token}` },
      data,
    });
    return response;
  } catch (error) {
    console.error('controller: DELETE error:', error.response?.data || error.message);
    throw error;
  }
};

// Специализированные методы для SupportPage (глобальный каталог)
controller.createUidManual = async (body) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.post(normalizeUrl(`${baseUrl}/api/catalog/uids/manual`), body, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

controller.updateUid = async (id, body) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.put(normalizeUrl(`${baseUrl}/api/catalog/uids/${id}`), body, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

controller.deleteUid = async (id) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.delete(normalizeUrl(`${baseUrl}/api/catalog/uids/${id}`), {
    headers: { Authorization: `Bearer ${token}` },
  });
};

controller.updateSuperAdmin = async (id, body) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.put(normalizeUrl(`${baseUrl}/api/catalog/superadmins/${id}`), body, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

controller.deleteSuperAdmin = async (id) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.delete(normalizeUrl(`${baseUrl}/api/catalog/superadmins/${id}`), {
    headers: { Authorization: `Bearer ${token}` },
  });
};

controller.updateProject = async (id, body) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.put(normalizeUrl(`${baseUrl}/api/catalog/projects/${id}`), body, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

controller.renameProject = async (id, body) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.patch(
    normalizeUrl(`${baseUrl}/api/catalog/projects/${id}/name`),
    body,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
};

controller.renameProjectDatabase = async (id, body) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.post(normalizeUrl(`${baseUrl}/api/catalog/projects/${id}/rename-db`), body, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

controller.deleteProject = async (id, body = {}) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.delete(normalizeUrl(`${baseUrl}/api/catalog/projects/${id}`), {
    headers: { Authorization: `Bearer ${token}` },
    data: body,
  });
};

controller.getSupportProjectMetrics = async (projectSlug) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const slug = String(projectSlug || '').trim();
  if (!slug) throw new Error('projectSlug is required');
  const url = normalizeUrl(
    `${FRONT_BASE}/api/p/${encodeURIComponent(slug)}/support-metrics`,
  );
  return axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

controller.createProjectTransferRequest = async (projectId, body) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const safeProjectId = Number(projectId);
  if (!Number.isFinite(safeProjectId)) throw new Error('projectId is required');
  const baseUrl = window.location.origin;
  return axios.post(
    normalizeUrl(`${baseUrl}/api/catalog/projects/${safeProjectId}/transfer-requests`),
    body || {},
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
};

controller.listProjectTransferRequests = async (direction = 'incoming') => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.get(
    normalizeUrl(
      `${baseUrl}/api/catalog/project-transfer-requests?direction=${encodeURIComponent(direction)}`,
    ),
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
};

controller.acceptProjectTransferRequest = async (requestId) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.post(
    normalizeUrl(
      `${baseUrl}/api/catalog/project-transfer-requests/${Number(requestId)}/accept`,
    ),
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
};

controller.rejectProjectTransferRequest = async (requestId) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.post(
    normalizeUrl(
      `${baseUrl}/api/catalog/project-transfer-requests/${Number(requestId)}/reject`,
    ),
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
};

controller.cancelProjectTransferRequest = async (requestId) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const baseUrl = window.location.origin;
  return axios.post(
    normalizeUrl(
      `${baseUrl}/api/catalog/project-transfer-requests/${Number(requestId)}/cancel`,
    ),
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
};

// ============================================================================
// Project backup (phase 1-2 of docs/04-project/PROJECT_BACKUP_PLAN.md)
// Three thin wrappers around /api/catalog/projects/:slug/backup/*.
// ============================================================================

controller.exportProjectBackup = async (
  projectSlug,
  { includeSecrets = false, dataMode = 'none', days } = {},
) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const slug = String(projectSlug || '').trim();
  if (!slug) throw new Error('projectSlug is required');
  const baseUrl = window.location.origin;
  const params = new URLSearchParams();
  if (includeSecrets) params.set('includeSecrets', '1');
  if (dataMode && dataMode !== 'none') params.set('dataMode', dataMode);
  if (dataMode === 'window') params.set('days', String(days || 1));
  const query = params.toString() ? `?${params.toString()}` : '';
  return axios.get(
    normalizeUrl(
      `${baseUrl}/api/catalog/projects/${encodeURIComponent(slug)}/backup/export${query}`,
    ),
    {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    },
  );
};

async function readFileAsBase64(file) {
  // Build base64 off the file bytes without going through atob/btoa so the
  // zip survives binary payloads unchanged.
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return window.btoa(binary);
}

controller.previewProjectBackup = async (projectSlug, file) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const slug = String(projectSlug || '').trim();
  if (!slug) throw new Error('projectSlug is required');
  if (!file) throw new Error('file is required');
  const zip_base64 = await readFileAsBase64(file);
  const baseUrl = window.location.origin;
  return axios.post(
    normalizeUrl(
      `${baseUrl}/api/catalog/projects/${encodeURIComponent(slug)}/backup/preview`,
    ),
    { zip_base64 },
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
};

controller.importProjectBackup = async (projectSlug, file) => {
  const token = getToken();
  if (!token) throw new Error('No token found');
  const slug = String(projectSlug || '').trim();
  if (!slug) throw new Error('projectSlug is required');
  if (!file) throw new Error('file is required');
  const zip_base64 = await readFileAsBase64(file);
  const baseUrl = window.location.origin;
  return axios.post(
    normalizeUrl(
      `${baseUrl}/api/catalog/projects/${encodeURIComponent(slug)}/backup/import`,
    ),
    { zip_base64 },
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
};

// Устанавливаем интерцепторы в самом конце, после создания всех контроллеров
installInterceptorsOnce();

export { controller, controllerPut, globalController, projectController };
